import type { ReactNode } from 'react'
import { DocsCallout, DocsContent, DocsLayout, DocsStepList, H2, H3 } from '@/components/docs'

/** One paragraph of the page, in the docs pages' body style. */
function P({ children }: { children: ReactNode }) {
  return <p className="mb-4 leading-relaxed text-foreground">{children}</p>
}

/** A UI label as the app writes it. */
function L({ children }: { children: ReactNode }) {
  return <strong className="text-foreground">{children}</strong>
}

/** A bulleted list of short facts; each child is one `<li>`. */
function Facts({ children }: { children: ReactNode }) {
  return <ul className="mb-6 list-disc space-y-2 pl-6 text-foreground">{children}</ul>
}

function CaseMemorySection() {
  return (
    <>
      <H2 id="case-memory">Case memory and the received date</H2>
      <P>
        Every change to a case graph is written to the case's change log as it happens: an entity
        mentioned by a source, a relationship added or removed, a property changed, two entities
        merged, a fact added by an analyst. Each entry carries two dates: when it happened (where
        the source says so) and when the case <em>learned</em> it. The log is append-only; an undo
        is a new entry.
      </P>
      <P>
        The day the case learned something is the <L>received date</L> of its source. Set it in the
        upload dialog (<L>Received on</L>, for the whole batch), over SFTP by putting the files in a
        first-level folder named <code>YYYY-MM-DD</code> inside the case's folder, or later in the
        document detail (<L>Received</L>, pencil). Without one it is the upload time. A future day
        is refused; a folder that is not a valid day gives the upload time.
      </P>
    </>
  )
}

function ThemeFileSection() {
  return (
    <>
      <H2 id="theme-file">Case overview</H2>
      <P>
        Every case opens on <L>Overview</L> and is a file over time. Create it with a name,
        description and any initial entities. The header reads <L>Kept since</L> for every case: the
        earlier of its creation date and its first source's received date.
      </P>
      <OverviewContents />
      <FileAsOfSection />
    </>
  )
}

function OverviewContents() {
  return (
    <>
      <Facts>
        <li>Header: status, refresh counts, PDF, upload, edit, case name, dates and File as of.</li>
        <li>
          Three tiles: sources, new objects and alerts, each with the last 30 days and a 12-month
          sparkline.
        </li>
        <li>
          Assistant: type a question, or pick What changed in the last month?, Summarise the open
          hypotheses or What is new about the watched objects?. The assistant opens with the
          question sent.
        </li>
        <li>
          Sources per month, stacked by kind, with the alerts line and Chart or Table; beside it the
          open hypotheses and latest assessment note.
        </li>
        <li>
          Monitor lists: what's new, watched objects and standing sources, each linking to the
          Monitor.
        </li>
        <li>About the case: editable description, saved investigations and entity resolution.</li>
      </Facts>
      <P>
        Before the first source, a start card with <L>Upload documents</L> replaces the chart and
        the Monitor lists are hidden. The case roadmap appears under the header when enabled in case
        settings. It disappears once the first source arrives.
      </P>
    </>
  )
}

function FileAsOfSection() {
  return (
    <>
      <H3 id="file-as-of">File as of a date</H3>
      <P>
        <L>File as of</L> in the Overview header shows the case as it stood at the end of a past
        day. The date stays set while you move through the case: the header dates, tiles, chart,
        hypotheses, latest note, Monitor lists and every entity's History card read as of it, under
        a banner with <L>Back to today</L>. The assistant and About the case stay current; ready
        questions do not pass this date to the assistant. The graph, timeline, map and Monitor inbox
        also stay current. Hypotheses take no new assessment while a date is set.
      </P>
    </>
  )
}

function MonitorSection() {
  return (
    <>
      <H2 id="monitor">Monitor</H2>
      <P>
        The Monitor alerts the case when something it watches shows up in a new source. Press{' '}
        <L>Watch</L> on an entity in the graph sidebar or on a person's identity page: it goes on
        <L>Watched</L>, and <L>Mentioned in a new source</L> alerts on every new mention. On the
        Monitor's <L>Rules</L> tab make the other rules, each under a name of its own:
      </P>
      <Facts>
        <li>
          <L>Phrase in a new source</L>: whole words, case and diacritics ignored, searched in
          documents, table rows, chats and transcripts once the source is processed.
        </li>
        <li>
          <L>New connection between watched entities</L>: a new source links two watched entities.
        </li>
        <li>
          <L>Change on a watched entity</L>: a property changes, or Xendrel merges another entity
          into it. Two watched entities merged into one raise one <L>Now one entity</L> alert.
        </li>
      </Facts>
      <P>
        One alert per rule, object and source, dated by the source's received date. The inbox is
        shared by the case; collaborators triage each alert: <L>Accept into an investigation…</L>{' '}
        (pins the entity and its source), <L>Dismiss</L>, <L>Snooze</L> (a day, a week, a month) or{' '}
        <L>Reopen</L>. The Monitor item of the case menu counts the alerts raised since your last
        visit and updates live. The <L>Sources</L> tab lists each upload and SFTP folder with its
        last intake.
      </P>
      <DocsCallout variant="note" title="Rules live on the default list">
        The rule form adds every rule to <L>Watched</L>. A phrase on a list of your own is searched
        only by a phrase rule of that list, so put the phrases you want found on Watched or into the
        rule itself.
      </DocsCallout>
    </>
  )
}

function HistorySection() {
  return (
    <>
      <H2 id="entity-history">Entity history and analyst facts</H2>
      <P>
        The entity detail (graph sidebar and identity page) carries a <L>History</L> card: first and
        last seen, the number of sources, mentions per month, the last change, conflicting values
        with each source, and the events newest first, each opening its source. Events of entities
        merged into it are included. A <L>Changed</L> badge marks entities merged or changed in the
        last 30 days in the graph's lists and on the canvas.
      </P>
      <DocsStepList
        steps={[
          {
            title: 'Add fact',
            description:
              'Records a value with its source and validity and pins it: later documents and merges leave it, and what they say instead shows in the history as kept out.',
          },
          {
            title: 'No longer valid since…',
            description:
              'Marks a property or a relationship as no longer holding from a date, with a reason. It stays in the graph, hidden by default; Show no longer valid shows it greyed, Valid again undoes it.',
          },
          {
            title: 'Mark wrong',
            description:
              'Retires an entity or relationship with a reason, together with the entities only it vouched for. Restore brings back exactly those.',
          },
        ]}
      />
    </>
  )
}

function HypothesesSection() {
  return (
    <>
      <H2 id="hypotheses">Hypotheses</H2>
      <P>
        Hypotheses are on the Investigations page, above the investigation cards. A hypothesis has a
        title, a claim and focus entities. Each assessment adds a verdict (<L>Supported</L>,{' '}
        <L>Refuted</L>, <L>Inconclusive</L>), a confidence, a rationale and cited evidence
        (investigations, documents, alerts, research runs). Assessments are never edited; the
        evolution strip shows how the judgment moved, its height the confidence.
      </P>
      <P>
        <L>Start research</L> launches a Deep Research run seeded with the claim and the focus
        entities. Research returns a cited report, never a verdict; a finding saved from it becomes
        evidence for the hypothesis. Investigation notes of the kind <L>Assessment</L> show on the
        hypothesis and on the Overview, and the case summary lists the hypotheses with their
        verdicts.
      </P>
    </>
  )
}

export default function DocsLongHorizonFile() {
  return (
    <DocsLayout>
      <DocsContent
        title="Long-horizon File"
        description="Keep a case as a standing file: every change logged with its source and the day the case learned it, one Overview for every case, the file as of any date, alerts from the Monitor, entity histories with analyst facts, and hypotheses with their assessments."
        previousPage={{ title: 'Timeline View', href: '/docs/timeline-view' }}
      >
        <CaseMemorySection />
        <ThemeFileSection />
        <MonitorSection />
        <HistorySection />
        <HypothesesSection />
      </DocsContent>
    </DocsLayout>
  )
}
